import { Button, css, Inline, styled } from 'canoe';
import isNumber from 'lodash/isNumber';
import React, { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import { ValueBox, ValueGroup, ValueStepper } from '@candu-ai/craft/ui';
import { SizeType } from 'craft/components/Size';
import { SizeTypeSelector } from 'craft/containers/Toolbox/sections/Size/SizeTypeSelector';

import { CSSValue } from './Spacing';

const PopupOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const Popup = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 50px;
    background: ${theme.colors.White};
    border: 1px solid ${theme.colors.Success};
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spaces.xs};
    position: absolute;
    top: 128px;
  `}
`;

const StyledButton = styled(Button)`
  ${({ theme }) => css`
    width: 46px;
    height: 32px;
    padding: ${theme.spaces.s} 0;
    border: 1px solid ${theme.colors.GlitterGray};
    background-color: ${theme.colors.GlitterGrayL20};

    &:hover,
    &:focus,
    &:active {
      border-color: ${theme.colors.GlitterGray};
      box-shadow: ${theme.boxShadow.outline(theme.colors.GlitterGray)};
      background-color: ${theme.colors.GlitterGray};
    }
  `}
`;

const parseValue = (value?: CSSValue) => {
  if (value === 'auto') {
    return { type: SizeType.Auto, value: undefined };
  }
  // default to PX if value is numeric
  if (isNumber(value)) {
    return { type: SizeType.Pixel, value };
  }
  // since we know it's not a number, we're not getting a false positive from a 0
  if (!value) {
    return { type: undefined, value: undefined };
  }

  // handle string values
  const parsed = parseInt(value.replace(/px|%/, ''), 10);

  // fallback to unset if something goes wrong
  if (Number.isNaN(parsed)) {
    return { type: undefined, value: undefined };
  }

  if (value.indexOf('%') > 0) {
    return { type: SizeType.Percentage, value: parsed };
  }
  return { type: SizeType.Pixel, value: parsed };
};

interface SpacingPopupProps {
  onClose: VoidFunction;
  setValue: (value: string | undefined) => void;
  onKeyDown: KeyboardEventHandler;
  showAuto: boolean;
  showPercentage: boolean;
  value?: CSSValue;
  showAsEmpty: (empty: boolean) => void;
  allowNegativeValues?: boolean;
}

export const EditSpacingPopup = ({
  onClose,
  setValue,
  onKeyDown,
  showAuto,
  showPercentage,
  value,
  showAsEmpty,
  allowNegativeValues,
}: SpacingPopupProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const stepperValue = parseValue(value);

  const [defaultValue] = useState<number | undefined>(stepperValue.value);
  const [defaultType] = useState<SizeType>(stepperValue.type ?? SizeType.Pixel);
  const [inputValue, setInputValue] = useState<number | undefined>(stepperValue.value);
  const [sizeType, setSizeType] = useState<SizeType | undefined>(
    stepperValue.type ?? SizeType.Pixel,
  );

  useEffect(() => {
    selectInput();
  }, []);

  const onOutsideClick = () => {
    onBlur();
    onClose();
  };

  const selectInput = () => {
    inputRef.current?.select();
  };

  const onSetType = (type: SizeType) => {
    if (type === SizeType.Auto) {
      setValue('auto');
    } else {
      setValue(
        isNumber(inputValue) ? `${inputValue}${type === SizeType.Percentage ? '%' : 'px'}` : '0px',
      );
      setSizeType(type);
      selectInput();
    }
  };

  const onSetValue = (val?: number) => {
    setInputValue(val);

    if (typeof sizeType === 'undefined' || sizeType === SizeType.Auto) {
      // if a number has been typed but there is no size type selected, default to pixel
      setSizeType(SizeType.Pixel);
    }

    setValue(isNumber(val) ? `${val}${sizeType === SizeType.Percentage ? '%' : 'px'}` : '0px');
  };

  const onBlur = () => {
    if (typeof inputValue === 'undefined' && sizeType !== SizeType.Auto) {
      // on delete value, revert to original
      setInputValue(defaultValue);
      setSizeType(defaultType);

      if (defaultType === SizeType.Auto) {
        setValue('auto');
      } else if (!defaultType) {
        setValue(undefined);
      } else {
        setValue(
          isNumber(defaultValue)
            ? `${defaultValue}${defaultType === SizeType.Percentage ? '%' : 'px'}`
            : undefined,
        );
      }
    }
  };

  useEffect(() => {
    if (typeof stepperValue.value === 'undefined' && typeof stepperValue.type === 'undefined') {
      // if value is unset, set the default to 0 Pixels
      setInputValue(0);
      setSizeType(SizeType.Pixel);
      showAsEmpty(false);
    } else if (typeof inputValue === 'undefined' && stepperValue.value === 0) {
      // if the input value is being deleted, reflect empty value is spacing box without changing the value
      showAsEmpty(true);
    } else {
      // reflect spacing value changes in the form
      setInputValue(stepperValue.value);
      setSizeType(stepperValue.type ?? SizeType.Pixel);
      showAsEmpty(false);
    }
  }, [stepperValue, inputValue]);

  return (
    <OutsideClickHandler onOutsideClick={onOutsideClick}>
      <PopupOverlay onClick={onOutsideClick}>
        <Popup
          onClick={(e) => {
            e.stopPropagation();
            onBlur();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onBlur();
            }
            onKeyDown(e);
          }}
        >
          <Inline space="xxs" align="center" justify="space-between" wrap="nowrap">
            <div role="none" onClick={(e) => e.stopPropagation()}>
              <ValueGroup mb="none">
                <ValueBox>
                  <ValueStepper
                    ref={inputRef}
                    step={1}
                    editable
                    format="inline"
                    minValue={allowNegativeValues ? -9999 : 0}
                    maxValue={9999}
                    onChange={(newValue?: number) => onSetValue(newValue)}
                    value={inputValue}
                    placeholder={
                      sizeType === SizeType.Auto && !inputValue?.toString() ? 'Auto' : undefined
                    }
                    autoFocus
                  />
                  <SizeTypeSelector
                    value={sizeType}
                    onChange={onSetType}
                    data-cy="toolbox-spacing"
                    standalone
                    showAuto={showAuto}
                    showPercentage={showPercentage}
                    property="spacing"
                  />
                </ValueBox>
              </ValueGroup>
            </div>

            <StyledButton
              variant="neutral"
              size="xsmall"
              fontWeight="normal"
              onClick={(e) => {
                e.stopPropagation();
                setValue(undefined);
              }}
            >
              Reset
            </StyledButton>
          </Inline>
        </Popup>
      </PopupOverlay>
    </OutsideClickHandler>
  );
};
